import React, { useEffect, useMemo, useState } from 'react';
import { onValue, ref } from 'firebase/database';
import { auth, database } from '../firebase';

type ProjectNode = Record<string, unknown>;

type MetricsRow = {
  projectId: string;
  email: string;
  createdDate: string;
  fullDate: string;
  paymentDate: string;
  createdTimestamp: number;
  fullTimestamp: number;
  paymentTimestamp: number;
  converted: boolean;
  previewCost: number;
  fullCost: number;
  regenerationCost: number;
  vectorizerCost: number;
  vectorizerCredits: number;
  totalCost: number;
  boldRevenue: number;
  boldRevenueCurrency: string;
  adminRevenue: number;
  adminRevenueCurrency: string;
  netMargin: number;
  status: string;
  boldStatus: string;
  type: string;
  typePrice: string;
};

type DailyMetrics = {
  date: string;
  previews: number;
  purchased: number;
  abandoned: number;
  conversionRate: number;
  previewCost: number;
  fullCost: number;
  regenerationCost: number;
  totalCost: number;
  costCurrency: MoneyCurrency;
  boldRevenue: number | null;
  boldRevenueCurrency: MoneyCurrency | null;
  adminRevenue: number | null;
  adminRevenueCurrency: MoneyCurrency | null;
  totalRevenue: number | null;
  totalRevenueCurrency: MoneyCurrency | null;
  netMargin: number | null;
  netMarginCurrency: MoneyCurrency | null;
};

type GroupMode = 'day' | 'week' | 'month' | 'year';
type MoneyCurrency = 'COP' | 'USD';
type DisplayCurrency = 'ORIGINAL' | MoneyCurrency;

type FirebaseCostGroup = {
  key: string;
  grossCost: number;
  savings: number;
  totalCost: number;
  services: Array<{
    service: string;
    grossCost: number;
    savings: number;
    totalCost: number;
  }>;
};

type FirebaseCostsResponse = {
  currency: string;
  startDate: string;
  endDate: string;
  groupBy: GroupMode;
  grossCost: number;
  savings: number;
  totalCost: number;
  services: Array<{
    service: string;
    grossCost: number;
    savings: number;
    totalCost: number;
  }>;
  groups: FirebaseCostGroup[];
};

const firebaseCostsEndpoint = 'https://firebase-costs-slyn3k2c5q-uc.a.run.app/firebase_costs';
// TODO: Replace this with real Brevo emailing costs if billing data becomes available.
const brevoEmailingCost = 0;
const excludedGoogleCloudServices = new Set(['Gemini API']);

const extractTotalCost = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (!value || typeof value !== 'object') {
    return 0;
  }

  const entry = value as Record<string, unknown>;
  const direct = entry.total_cost;
  if (typeof direct === 'number' && Number.isFinite(direct)) {
    return direct;
  }

  return 0;
};

const sumCostNode = (node: unknown): number => {
  if (!node || typeof node !== 'object') {
    return 0;
  }

  return Object.values(node as Record<string, unknown>).reduce<number>(
    (total, item) => total + extractTotalCost(item),
    0
  );
};

const sumMatchingNodes = (project: ProjectNode, pattern: RegExp): number =>
  Object.entries(project).reduce((total, [key, value]) => {
    if (!pattern.test(key)) {
      return total;
    }
    return total + sumCostNode(value);
  }, 0);

const getVectorizerMetrics = (project: ProjectNode): { cost: number; credits: number } => {
  const node = project.VectorizerAI;
  if (!node || typeof node !== 'object') {
    return { cost: 0, credits: 0 };
  }

  const total = { cost: 0, credits: 0 };
  const visit = (value: unknown) => {
    if (!value || typeof value !== 'object') {
      return;
    }

    const entry = value as Record<string, unknown>;
    if ('credits' in entry || 'total_cost' in entry) {
      total.cost += extractTotalCost(entry);
      total.credits += getNumber(entry.credits);
      return;
    }

    Object.values(entry).forEach(visit);
  };

  visit(node);
  return total;
};

const getNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const normalizeUnixTimestampToSeconds = (timestamp: number): number => {
  const timestampText = String(Math.trunc(timestamp));
  if (timestampText.length > 13) {
    return Number(timestampText.slice(0, 10));
  }
  if (timestamp > 100000000000) {
    return Math.trunc(timestamp / 1000);
  }
  return timestamp;
};

const getText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const formatDateTime = (timestamp: number): string => {
  if (!timestamp) {
    return '-';
  }
  return new Intl.DateTimeFormat('es-CO', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Bogota',
  }).format(new Date(timestamp * 1000));
};

const formatPaymentDate = (timestamp: number): string => {
  if (!timestamp) {
    return '-';
  }
  return new Intl.DateTimeFormat('es-CO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/Bogota',
  }).format(new Date(timestamp * 1000));
};

const getDateParts = (timestamp: number): Record<string, string> => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/Bogota',
  }).formatToParts(new Date(timestamp * 1000));
  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
};

const toLocalDate = (timestamp: number): Date => {
  const parts = getDateParts(timestamp);
  return new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
};

const getIsoWeek = (timestamp: number): string => {
  const date = toLocalDate(timestamp);
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
};

const toGroupKey = (timestamp: number, groupMode: GroupMode): string => {
  if (!timestamp) {
    return 'sin_fecha';
  }
  const values = getDateParts(timestamp);
  if (groupMode === 'year') {
    return values.year;
  }
  if (groupMode === 'month') {
    return `${values.year}-${values.month}`;
  }
  if (groupMode === 'week') {
    return getIsoWeek(timestamp);
  }
  return `${values.year}-${values.month}-${values.day}`;
};

const formatUsd = (value: number): string => {
  const precision = value !== 0 && Math.abs(value) < 0.1 ? 4 : 1;
  return `USD ${value.toFixed(precision)}`;
};

const formatCop = (value: number): string =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);

const formatCurrency = (value: number, currency: MoneyCurrency): string =>
  currency === 'COP' ? formatCop(value) : formatUsd(value);

const normalizeMoneyCurrency = (currency: string): MoneyCurrency | null => {
  const normalized = currency.toUpperCase();
  return normalized === 'COP' || normalized === 'USD' ? normalized : null;
};

const formatOptionalCurrency = (value: number | null, currency: MoneyCurrency | null): string => {
  if (value === null || !currency) {
    return '-';
  }
  return formatCurrency(value, currency);
};

const getGeminiCostValue = (
  costUsd: number,
  displayCurrency: DisplayCurrency,
  usdToCop: number
): { value: number; currency: MoneyCurrency } => {
  if (displayCurrency === 'COP') {
    return { value: costUsd * usdToCop, currency: 'COP' };
  }
  return { value: costUsd, currency: 'USD' };
};

const getRevenueValue = (
  revenue: number,
  revenueCurrency: string,
  displayCurrency: DisplayCurrency,
  usdToCop: number
): { value: number | null; currency: MoneyCurrency | null } => {
  const normalized = normalizeMoneyCurrency(revenueCurrency);
  if (displayCurrency === 'ORIGINAL') {
    return { value: normalized ? revenue : null, currency: normalized };
  }
  return { value: convertMoney(revenue, revenueCurrency, displayCurrency, usdToCop), currency: displayCurrency };
};

const addMoney = (
  currentValue: number | null,
  currentCurrency: MoneyCurrency | null,
  nextValue: number | null,
  nextCurrency: MoneyCurrency | null
): { value: number | null; currency: MoneyCurrency | null } => {
  if (nextValue === null || !nextCurrency) {
    return { value: currentValue, currency: currentCurrency };
  }
  if (nextValue === 0) {
    return { value: currentValue, currency: currentCurrency };
  }
  if (currentValue === null) {
    return { value: null, currency: null };
  }
  if (currentCurrency === null) {
    return { value: nextValue, currency: nextCurrency };
  }
  if (currentCurrency !== nextCurrency) {
    return { value: null, currency: null };
  }
  return { value: currentValue + nextValue, currency: currentCurrency };
};

const sumTwoMoneyValues = (
  firstValue: number | null,
  firstCurrency: MoneyCurrency | null,
  secondValue: number | null,
  secondCurrency: MoneyCurrency | null
): { value: number | null; currency: MoneyCurrency | null } => {
  const first = addMoney(0, null, firstValue, firstCurrency);
  return addMoney(first.value, first.currency, secondValue, secondCurrency);
};

const getExternalCostValue = (
  value: number,
  sourceCurrency: string,
  displayCurrency: DisplayCurrency,
  usdToCop: number
): { value: number | null; currency: MoneyCurrency | null } => {
  const normalized = normalizeMoneyCurrency(sourceCurrency);
  if (displayCurrency === 'ORIGINAL') {
    return { value: normalized ? value : null, currency: normalized };
  }
  return { value: convertMoney(value, sourceCurrency, displayCurrency, usdToCop), currency: displayCurrency };
};

const convertMoney = (
  value: number,
  sourceCurrency: string,
  targetCurrency: MoneyCurrency,
  usdToCop: number
): number | null => {
  const source = normalizeMoneyCurrency(sourceCurrency);
  if (!source || usdToCop <= 0) {
    return null;
  }
  if (source === targetCurrency) {
    return value;
  }
  if (source === 'USD' && targetCurrency === 'COP') {
    return value * usdToCop;
  }
  if (source === 'COP' && targetCurrency === 'USD') {
    return value / usdToCop;
  }
  return null;
};

const escapeXml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const toWorksheetXml = (sheetName: string, headers: string[], rows: Array<Record<string, unknown>>): string => {
  const headerRow = headers.map((header) => `<Cell><Data ss:Type="String">${escapeXml(header)}</Data></Cell>`).join('');
  const dataRows = rows
    .map((row) =>
      `<Row>${headers
        .map((header) => `<Cell><Data ss:Type="String">${escapeXml(row[header])}</Data></Cell>`)
        .join('')}</Row>`
    )
    .join('');

  return `<Worksheet ss:Name="${escapeXml(sheetName)}"><Table><Row>${headerRow}</Row>${dataRows}</Table></Worksheet>`;
};

const downloadExcelWorkbook = (
  filename: string,
  sheets: Array<{ name: string; headers: string[]; rows: Array<Record<string, unknown>> }>
) => {
  const workbook = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:x="urn:schemas-microsoft-com:office:excel"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  ${sheets.map((sheet) => toWorksheetXml(sheet.name, sheet.headers, sheet.rows)).join('')}
</Workbook>`;
  const blob = new Blob([workbook], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const getBogotaDateKey = (date: Date): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/Bogota',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const getMonthStart = (dateKey: string): string => `${dateKey.slice(0, 8)}01`;

const isDateInRange = (dateKey: string, startDate: string, endDate: string): boolean => {
  if (!dateKey || dateKey === 'sin_fecha') {
    return false;
  }
  return dateKey >= startDate && dateKey <= endDate;
};

const formatBackendCurrency = (value: number, currency: string): string => {
  const display = normalizeMoneyCurrency(currency);
  if (display) {
    return formatCurrency(value, display);
  }
  return `${currency} ${value.toFixed(2)}`;
};

const isExcludedGoogleCloudService = (service: string): boolean => excludedGoogleCloudServices.has(service);

const mapProjectToMetricsRow = (projectId: string, project: ProjectNode): MetricsRow | null => {
  const createdTimestamp = getNumber(project.created);
  const fullTimestamp = getNumber(project.start_full);
  const previewCost = sumMatchingNodes(project, /^Preview_\d+$/i);
  const fullCost = sumCostNode(project.Full ?? project.full);
  const regenerationCost = sumMatchingNodes(project, /^Regeneration_/i);
  const vectorizer = getVectorizerMetrics(project);
  const totalCost = previewCost + fullCost + regenerationCost;
  const payment = project.Payment && typeof project.Payment === 'object'
    ? (project.Payment as Record<string, unknown>)
    : {};
  const adminPayment = project.AdminPayment && typeof project.AdminPayment === 'object'
    ? (project.AdminPayment as Record<string, unknown>)
    : {};
  const boldRevenue = getNumber(payment.value);
  const paymentTimestamp = normalizeUnixTimestampToSeconds(getNumber(payment.timestamp));
  const adminRevenue = getNumber(adminPayment.value);
  const status = getText(project.status);
  const boldStatus = getText(project.bold_status);
  const boldRevenueCurrency = getText(payment.currency) || 'COP';
  const adminRevenueCurrency = getText(adminPayment.currency) || 'COP';
  const converted = status === 'Completed' || adminRevenue > 0;

  return {
    projectId,
    email: getText(project.Email),
    createdDate: toGroupKey(createdTimestamp, 'day'),
    fullDate: toGroupKey(fullTimestamp, 'day'),
    paymentDate: toGroupKey(paymentTimestamp, 'day'),
    createdTimestamp,
    fullTimestamp,
    paymentTimestamp,
    converted,
    previewCost,
    fullCost,
    regenerationCost,
    vectorizerCost: vectorizer.cost,
    vectorizerCredits: vectorizer.credits,
    totalCost,
    boldRevenue,
    boldRevenueCurrency,
    adminRevenue,
    adminRevenueCurrency,
    netMargin: 0,
    status,
    boldStatus,
    type: getText(project.type),
    typePrice: getText(project.type_price),
  };
};

const aggregateMetrics = (
  rows: MetricsRow[],
  groupMode: GroupMode,
  displayCurrency: DisplayCurrency,
  usdToCop: number
): DailyMetrics[] => {
  const byDate = new Map<string, DailyMetrics>();

  rows.forEach((row) => {
    if (!row.createdTimestamp) {
      return;
    }
    const key = toGroupKey(row.createdTimestamp, groupMode);
    const previewCost = getGeminiCostValue(row.previewCost, displayCurrency, usdToCop);
    const fullCost = getGeminiCostValue(row.fullCost, displayCurrency, usdToCop);
    const regenerationCost = getGeminiCostValue(row.regenerationCost, displayCurrency, usdToCop);
    const totalCost = getGeminiCostValue(row.totalCost, displayCurrency, usdToCop);
    const boldRevenue = getRevenueValue(row.boldRevenue, row.boldRevenueCurrency, displayCurrency, usdToCop);
    const adminRevenue = getRevenueValue(row.adminRevenue, row.adminRevenueCurrency, displayCurrency, usdToCop);
    const totalRevenue = sumTwoMoneyValues(
      boldRevenue.value,
      boldRevenue.currency,
      adminRevenue.value,
      adminRevenue.currency
    );
    const current = byDate.get(key) ?? {
      date: key,
      previews: 0,
      purchased: 0,
      abandoned: 0,
      conversionRate: 0,
      previewCost: 0,
      fullCost: 0,
      regenerationCost: 0,
      totalCost: 0,
      costCurrency: totalCost.currency,
      boldRevenue: 0,
      boldRevenueCurrency: null,
      adminRevenue: 0,
      adminRevenueCurrency: null,
      totalRevenue: 0,
      totalRevenueCurrency: null,
      netMargin: 0,
      netMarginCurrency: totalCost.currency,
    };

    current.previews += 1;
    current.purchased += row.converted ? 1 : 0;
    current.abandoned += row.converted ? 0 : 1;
    current.previewCost += previewCost.value;
    current.fullCost += fullCost.value;
    current.regenerationCost += regenerationCost.value;
    current.totalCost += totalCost.value;
    const nextBoldRevenue = addMoney(
      current.boldRevenue,
      current.boldRevenueCurrency,
      boldRevenue.value,
      boldRevenue.currency
    );
    current.boldRevenue = nextBoldRevenue.value;
    current.boldRevenueCurrency = nextBoldRevenue.currency;
    const nextAdminRevenue = addMoney(
      current.adminRevenue,
      current.adminRevenueCurrency,
      adminRevenue.value,
      adminRevenue.currency
    );
    current.adminRevenue = nextAdminRevenue.value;
    current.adminRevenueCurrency = nextAdminRevenue.currency;
    const nextTotalRevenue = addMoney(
      current.totalRevenue,
      current.totalRevenueCurrency,
      totalRevenue.value,
      totalRevenue.currency
    );
    current.totalRevenue = nextTotalRevenue.value;
    current.totalRevenueCurrency = nextTotalRevenue.currency;
    if (current.totalRevenue !== null && current.totalRevenueCurrency === current.costCurrency) {
      current.netMargin = current.totalRevenue - current.totalCost;
      current.netMarginCurrency = current.costCurrency;
    } else {
      current.netMargin = null;
      current.netMarginCurrency = null;
    }
    current.conversionRate = current.previews ? (current.purchased / current.previews) * 100 : 0;

    byDate.set(key, current);
  });

  return Array.from(byDate.values()).sort((a, b) => b.date.localeCompare(a.date));
};

function MetricsSection() {
  const todayKey = getBogotaDateKey(new Date());
  const [rows, setRows] = useState<MetricsRow[]>([]);
  const [displayCurrency, setDisplayCurrency] = useState<DisplayCurrency>('ORIGINAL');
  const [usdToCopInput, setUsdToCopInput] = useState('4000');
  const [groupMode, setGroupMode] = useState<GroupMode>('day');
  const [startDate, setStartDate] = useState(getMonthStart(todayKey));
  const [endDate, setEndDate] = useState(todayKey);
  const [firebaseCosts, setFirebaseCosts] = useState<FirebaseCostsResponse | null>(null);
  const [firebaseCostsStatus, setFirebaseCostsStatus] = useState('');
  const [isLoadingFirebaseCosts, setIsLoadingFirebaseCosts] = useState(false);
  const [hideZeroGoogleCloudRows, setHideZeroGoogleCloudRows] = useState(true);
  const [hideZeroGoogleCloudPeriodRows, setHideZeroGoogleCloudPeriodRows] = useState(true);
  const [hideZeroGroupedRows, setHideZeroGroupedRows] = useState(true);

  useEffect(() => {
    const projectsRef = ref(database, 'Projects');
    const unsubscribe = onValue(
      projectsRef,
      (snapshot) => {
        const value = (snapshot.val() ?? {}) as Record<string, ProjectNode>;
        const nextRows = Object.entries(value)
          .map(([projectId, project]) => mapProjectToMetricsRow(projectId, project))
          .filter((row): row is MetricsRow => row !== null)
          .sort((a, b) => b.createdTimestamp - a.createdTimestamp);
        setRows(nextRows);
      },
      () => {
        setRows([]);
      }
    );

    return () => unsubscribe();
  }, []);

  const loadFirebaseCosts = async () => {
    if (!startDate || !endDate) {
      setFirebaseCosts(null);
      setFirebaseCostsStatus('Selecciona un rango de fechas.');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setFirebaseCosts(null);
      setFirebaseCostsStatus('No hay sesión activa.');
      return;
    }

    setIsLoadingFirebaseCosts(true);
    setFirebaseCostsStatus('');
    try {
      const token = await currentUser.getIdToken();
      const params = new URLSearchParams({
        startDate,
        endDate,
        groupBy: groupMode,
      });
      const response = await fetch(`${firebaseCostsEndpoint}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = (await response.json().catch(() => ({}))) as FirebaseCostsResponse & { error?: unknown };

      if (!response.ok) {
        const errorMessage = typeof payload.error === 'string' ? payload.error : `Error ${response.status}`;
        setFirebaseCosts(null);
        setFirebaseCostsStatus(errorMessage);
        return;
      }

      setFirebaseCosts(payload);
      setFirebaseCostsStatus('');
    } catch (error) {
      setFirebaseCosts(null);
      setFirebaseCostsStatus('No se pudieron cargar los costos de Google Cloud.');
    } finally {
      setIsLoadingFirebaseCosts(false);
    }
  };

  const filteredRows = useMemo(
    () => rows.filter(
      (row) =>
        row.createdDate === 'sin_fecha' ||
        isDateInRange(row.createdDate, startDate, endDate) ||
        isDateInRange(row.paymentDate, startDate, endDate)
    ),
    [endDate, rows, startDate]
  );

  const usdToCop = useMemo(() => {
    const parsed = Number(usdToCopInput);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [usdToCopInput]);
  const dailyMetrics = useMemo(
    () => aggregateMetrics(filteredRows, groupMode, displayCurrency, usdToCop),
    [displayCurrency, filteredRows, groupMode, usdToCop]
  );
  const totals = useMemo(
    () => {
      const costCurrency: MoneyCurrency = displayCurrency === 'COP' ? 'COP' : 'USD';
      let boldRevenue: number | null = 0;
      let boldRevenueCurrency: MoneyCurrency | null = null;
      let adminRevenue: number | null = 0;
      let adminRevenueCurrency: MoneyCurrency | null = null;
      let totalRevenue: number | null = 0;
      let totalRevenueCurrency: MoneyCurrency | null = null;

      filteredRows.forEach((row) => {
        const nextBoldRevenue = getRevenueValue(row.boldRevenue, row.boldRevenueCurrency, displayCurrency, usdToCop);
        const nextAdminRevenue = getRevenueValue(row.adminRevenue, row.adminRevenueCurrency, displayCurrency, usdToCop);
        const nextTotalRevenue = sumTwoMoneyValues(
          nextBoldRevenue.value,
          nextBoldRevenue.currency,
          nextAdminRevenue.value,
          nextAdminRevenue.currency
        );
        const boldSum = addMoney(
          boldRevenue,
          boldRevenueCurrency,
          nextBoldRevenue.value,
          nextBoldRevenue.currency
        );
        boldRevenue = boldSum.value;
        boldRevenueCurrency = boldSum.currency;
        const adminSum = addMoney(
          adminRevenue,
          adminRevenueCurrency,
          nextAdminRevenue.value,
          nextAdminRevenue.currency
        );
        adminRevenue = adminSum.value;
        adminRevenueCurrency = adminSum.currency;
        const totalSum = addMoney(
          totalRevenue,
          totalRevenueCurrency,
          nextTotalRevenue.value,
          nextTotalRevenue.currency
        );
        totalRevenue = totalSum.value;
        totalRevenueCurrency = totalSum.currency;
      });

      return {
        previews: filteredRows.length,
        purchased: filteredRows.filter((row) => row.converted).length,
        geminiCost: filteredRows.reduce(
          (total, row) => total + getGeminiCostValue(row.totalCost, displayCurrency, usdToCop).value,
          0
        ),
        vectorizerCost: filteredRows.reduce((total, row) => total + row.vectorizerCost, 0),
        vectorizerCredits: filteredRows.reduce((total, row) => total + row.vectorizerCredits, 0),
        totalCost: filteredRows.reduce(
          (total, row) =>
            total +
            getGeminiCostValue(row.totalCost, displayCurrency, usdToCop).value +
            getGeminiCostValue(row.vectorizerCost, displayCurrency, usdToCop).value,
          0
        ),
        costCurrency,
        boldRevenue,
        boldRevenueCurrency,
        adminRevenue,
        adminRevenueCurrency,
        totalRevenue,
        totalRevenueCurrency,
      };
    },
    [displayCurrency, filteredRows, usdToCop]
  );
  const firebaseCurrency = firebaseCosts?.currency ?? 'USD';
  const includedGoogleCloudTotalCost = useMemo(
    () =>
      (firebaseCosts?.services ?? []).reduce(
        (total, service) => total + (isExcludedGoogleCloudService(service.service) ? 0 : service.totalCost),
        0
      ),
    [firebaseCosts]
  );
  const includedGoogleCloudGrossCost = useMemo(
    () =>
      (firebaseCosts?.services ?? []).reduce(
        (total, service) => total + (isExcludedGoogleCloudService(service.service) ? 0 : service.grossCost),
        0
      ),
    [firebaseCosts]
  );
  const includedGoogleCloudSavings = useMemo(
    () =>
      (firebaseCosts?.services ?? []).reduce(
        (total, service) => total + (isExcludedGoogleCloudService(service.service) ? 0 : service.savings),
        0
      ),
    [firebaseCosts]
  );
  const firebaseTotalCostValue = getExternalCostValue(
    includedGoogleCloudTotalCost,
    firebaseCurrency,
    displayCurrency,
    usdToCop
  );
  const firebaseGrossCostValue = getExternalCostValue(
    includedGoogleCloudGrossCost,
    firebaseCurrency,
    displayCurrency,
    usdToCop
  );
  const firebaseSavingsValue = getExternalCostValue(
    includedGoogleCloudSavings,
    firebaseCurrency,
    displayCurrency,
    usdToCop
  );
  const firebaseCurrencyMatchesDisplay = firebaseTotalCostValue.currency === totals.costCurrency;
  const firebaseTotalCost = firebaseTotalCostValue.value ?? 0;
  const firebaseGroupCostMap = useMemo(() => {
    return new Map(
      (firebaseCosts?.groups ?? []).map((group) => [
        group.key,
        getExternalCostValue(
          group.services.reduce(
            (total, service) => total + (isExcludedGoogleCloudService(service.service) ? 0 : service.totalCost),
            0
          ),
          firebaseCurrency,
          displayCurrency,
          usdToCop
        ).value ?? 0,
      ])
    );
  }, [displayCurrency, firebaseCosts, firebaseCurrency, usdToCop]);
  const firebaseServiceRows = useMemo(() => {
    return (firebaseCosts?.services ?? []).map((service) => ({
      Servicio: service.service,
      isExcluded: isExcludedGoogleCloudService(service.service),
      grossCost: service.grossCost,
      savings: service.savings,
      totalCost: service.totalCost,
      Bruto:
        displayCurrency === 'ORIGINAL'
          ? formatBackendCurrency(service.grossCost, firebaseCurrency)
          : formatOptionalCurrency(
              getExternalCostValue(service.grossCost, firebaseCurrency, displayCurrency, usdToCop).value,
              displayCurrency
            ),
      Ahorro:
        displayCurrency === 'ORIGINAL'
          ? formatBackendCurrency(service.savings, firebaseCurrency)
          : formatOptionalCurrency(
              getExternalCostValue(service.savings, firebaseCurrency, displayCurrency, usdToCop).value,
              displayCurrency
            ),
      Total:
        displayCurrency === 'ORIGINAL'
          ? formatBackendCurrency(service.totalCost, firebaseCurrency)
          : formatOptionalCurrency(
              getExternalCostValue(service.totalCost, firebaseCurrency, displayCurrency, usdToCop).value,
              displayCurrency
            ),
    }));
  }, [displayCurrency, firebaseCosts, firebaseCurrency, usdToCop]);
  const firebasePeriodServiceRows = useMemo(() => {
    return (firebaseCosts?.groups ?? []).flatMap((group) =>
      group.services.map((service) => ({
        Periodo: group.key,
        Servicio: service.service,
        isExcluded: isExcludedGoogleCloudService(service.service),
        grossCost: service.grossCost,
        savings: service.savings,
        totalCost: service.totalCost,
        Bruto:
          displayCurrency === 'ORIGINAL'
            ? formatBackendCurrency(service.grossCost, firebaseCurrency)
            : formatOptionalCurrency(
                getExternalCostValue(service.grossCost, firebaseCurrency, displayCurrency, usdToCop).value,
                displayCurrency
              ),
        Ahorro:
          displayCurrency === 'ORIGINAL'
            ? formatBackendCurrency(service.savings, firebaseCurrency)
            : formatOptionalCurrency(
                getExternalCostValue(service.savings, firebaseCurrency, displayCurrency, usdToCop).value,
                displayCurrency
              ),
        Total:
          displayCurrency === 'ORIGINAL'
            ? formatBackendCurrency(service.totalCost, firebaseCurrency)
            : formatOptionalCurrency(
                getExternalCostValue(service.totalCost, firebaseCurrency, displayCurrency, usdToCop).value,
                displayCurrency
              ),
      }))
    );
  }, [displayCurrency, firebaseCosts, firebaseCurrency, usdToCop]);
  const groupedMetricsWithFirebase = useMemo(() => {
    const byDate = new Map(
      dailyMetrics.map((day) => [
        day.date,
        {
          ...day,
          firebaseCost: firebaseGroupCostMap.get(day.date) ?? 0,
        },
      ])
    );

    firebaseGroupCostMap.forEach((firebaseCost, key) => {
      if (byDate.has(key)) {
        return;
      }
      byDate.set(key, {
        date: key,
        previews: 0,
        purchased: 0,
        abandoned: 0,
        conversionRate: 0,
        previewCost: 0,
        fullCost: 0,
        regenerationCost: 0,
        totalCost: 0,
        costCurrency: totals.costCurrency,
        boldRevenue: 0,
        boldRevenueCurrency: totals.costCurrency,
        adminRevenue: 0,
        adminRevenueCurrency: totals.costCurrency,
        totalRevenue: 0,
        totalRevenueCurrency: totals.costCurrency,
        netMargin: firebaseCurrencyMatchesDisplay ? -firebaseCost : null,
        netMarginCurrency: firebaseCurrencyMatchesDisplay ? totals.costCurrency : null,
        firebaseCost,
      });
    });

    return Array.from(byDate.values()).sort((a, b) => b.date.localeCompare(a.date));
  }, [dailyMetrics, firebaseCurrencyMatchesDisplay, firebaseGroupCostMap, totals.costCurrency]);
  const visibleGroupedMetrics = useMemo(
    () =>
      hideZeroGroupedRows
        ? groupedMetricsWithFirebase.filter(
            (day) =>
              day.previews !== 0 ||
              day.purchased !== 0 ||
              day.abandoned !== 0 ||
              day.previewCost !== 0 ||
              day.fullCost !== 0 ||
              day.regenerationCost !== 0 ||
              day.totalCost !== 0 ||
              day.firebaseCost !== 0 ||
              (day.boldRevenue ?? 0) !== 0 ||
              (day.adminRevenue ?? 0) !== 0 ||
              (day.totalRevenue ?? 0) !== 0 ||
              (day.netMargin ?? 0) !== 0
          )
        : groupedMetricsWithFirebase,
    [groupedMetricsWithFirebase, hideZeroGroupedRows]
  );
  const visibleFirebaseServiceRows = useMemo(
    () =>
      hideZeroGoogleCloudRows
        ? firebaseServiceRows.filter(
            (row) => row.grossCost !== 0 || row.savings !== 0 || row.totalCost !== 0
          )
        : firebaseServiceRows,
    [firebaseServiceRows, hideZeroGoogleCloudRows]
  );
  const visibleFirebasePeriodServiceRows = useMemo(
    () =>
      hideZeroGoogleCloudPeriodRows
        ? firebasePeriodServiceRows.filter(
            (row) => row.grossCost !== 0 || row.savings !== 0 || row.totalCost !== 0
          )
        : firebasePeriodServiceRows,
    [firebasePeriodServiceRows, hideZeroGoogleCloudPeriodRows]
  );
  const visibleProjectRows = filteredRows;
  const netMargin =
    totals.totalRevenue !== null &&
    totals.totalRevenueCurrency === totals.costCurrency &&
    firebaseCurrencyMatchesDisplay
      ? totals.totalRevenue - totals.totalCost - firebaseTotalCost
      : null;
  const conversionRate = totals.previews ? (totals.purchased / totals.previews) * 100 : 0;

  const exportProjectRows = visibleProjectRows.map((row) => {
    const previewCost = getGeminiCostValue(row.previewCost, displayCurrency, usdToCop);
    const fullCost = getGeminiCostValue(row.fullCost, displayCurrency, usdToCop);
    const regenerationCost = getGeminiCostValue(row.regenerationCost, displayCurrency, usdToCop);
    const totalCost = getGeminiCostValue(row.totalCost, displayCurrency, usdToCop);
    const boldRevenue = getRevenueValue(row.boldRevenue, row.boldRevenueCurrency, displayCurrency, usdToCop);
    const adminRevenue = getRevenueValue(row.adminRevenue, row.adminRevenueCurrency, displayCurrency, usdToCop);
    const totalRevenue = sumTwoMoneyValues(
      boldRevenue.value,
      boldRevenue.currency,
      adminRevenue.value,
      adminRevenue.currency
    );
    const margin =
      totalRevenue.value !== null && totalRevenue.currency === totalCost.currency
        ? totalRevenue.value - totalCost.value
        : null;

    return {
      Fecha: formatDateTime(row.createdTimestamp),
      'Project ID': row.projectId,
      'Compro': row.converted ? 'Si' : 'No',
      Moneda: displayCurrency,
      'Costo preview': formatCurrency(previewCost.value, previewCost.currency),
      'Costo full': formatCurrency(fullCost.value, fullCost.currency),
      'Costo regeneraciones': formatCurrency(regenerationCost.value, regenerationCost.currency),
      'Costo total IA': formatCurrency(totalCost.value, totalCost.currency),
      'Ingreso Bold': formatOptionalCurrency(boldRevenue.value, boldRevenue.currency),
      'Ingreso Directo': formatOptionalCurrency(adminRevenue.value, adminRevenue.currency),
      'Ingreso Total': formatOptionalCurrency(totalRevenue.value, totalRevenue.currency),
      'Margen IA': formatOptionalCurrency(margin, totalCost.currency),
      Status: row.status,
      'Bold status': row.boldStatus,
      Tipo: row.type,
      'Tipo precio': row.typePrice,
      'Moneda Bold original': row.boldRevenueCurrency,
      'Valor Bold original': row.boldRevenue,
      'Moneda Directo original': row.adminRevenueCurrency,
      'Valor Directo original': row.adminRevenue,
    };
  });

  const exportGroupedRows = visibleGroupedMetrics.map((day) => {
    const firebaseCost = day.firebaseCost;
    return {
      Grupo: day.date,
      Proyectos: day.previews,
      Compras: day.purchased,
      Abandonos: day.abandoned,
      'Conversion %': `${day.conversionRate.toFixed(2)}%`,
      Moneda: displayCurrency,
      'Costo preview': formatCurrency(day.previewCost, day.costCurrency),
      'Costo full': formatCurrency(day.fullCost, day.costCurrency),
      'Costo regeneraciones': formatCurrency(day.regenerationCost, day.costCurrency),
      'Costo IA': formatCurrency(day.totalCost, day.costCurrency),
      'Costo Google Cloud': formatOptionalCurrency(firebaseCost, firebaseTotalCostValue.currency),
      'Costo total': firebaseCurrencyMatchesDisplay
        ? formatCurrency(day.totalCost + firebaseCost, day.costCurrency)
        : '-',
      'Ingreso Bold': formatOptionalCurrency(day.boldRevenue, day.boldRevenueCurrency),
      'Ingreso Directo': formatOptionalCurrency(day.adminRevenue, day.adminRevenueCurrency),
      'Ingreso Total': formatOptionalCurrency(day.totalRevenue, day.totalRevenueCurrency),
      'Margen neto':
        day.totalRevenue !== null && day.totalRevenueCurrency === day.costCurrency && firebaseCurrencyMatchesDisplay
          ? formatCurrency(day.totalRevenue - day.totalCost - firebaseCost, day.costCurrency)
          : '-',
    };
  });
  const projectExportHeaders = [
    'Fecha',
    'Project ID',
    'Compro',
    'Moneda',
    'Costo preview',
    'Costo full',
    'Costo regeneraciones',
    'Costo total IA',
    'Ingreso Bold',
    'Ingreso Directo',
    'Ingreso Total',
    'Margen IA',
    'Status',
    'Bold status',
    'Tipo',
    'Tipo precio',
    'Moneda Bold original',
    'Valor Bold original',
    'Moneda Directo original',
    'Valor Directo original',
  ];
  const groupedExportHeaders = [
    'Grupo',
    'Proyectos',
    'Compras',
    'Abandonos',
    'Conversion %',
    'Moneda',
    'Costo preview',
    'Costo full',
    'Costo regeneraciones',
    'Costo IA',
    'Costo Google Cloud',
    'Costo total',
    'Ingreso Bold',
    'Ingreso Directo',
    'Ingreso Total',
    'Margen neto',
  ];
  const googleCloudExportHeaders = ['Servicio', 'Bruto', 'Ahorro', 'Total'];
  const googleCloudPeriodExportHeaders = ['Periodo', 'Servicio', 'Bruto', 'Ahorro', 'Total'];
  const hasExportData =
    exportProjectRows.length > 0 ||
    exportGroupedRows.length > 0 ||
    visibleFirebaseServiceRows.length > 0 ||
    visibleFirebasePeriodServiceRows.length > 0;

  return (
    <div className="section">
      <div className="section-header">
        <div>
          <h2>Métricas y Costos</h2>
        </div>
      </div>

      <div className="metrics-controls">
        <div className="metrics-control-row metrics-control-row-two">
          <label className="field">
            <span>Desde</span>
            <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
          </label>
          <label className="field">
            <span>Hasta</span>
            <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
          </label>
        </div>
        <div className="metrics-control-row metrics-control-row-two">
          <label className="field">
            <span>1 USD = COP</span>
            <input
              type="number"
              min="1"
              step="1"
              value={usdToCopInput}
              onChange={(event) => setUsdToCopInput(event.target.value)}
            />
          </label>
          <label className="field">
            <span>Moneda</span>
            <select
              value={displayCurrency}
              onChange={(event) => setDisplayCurrency(event.target.value as DisplayCurrency)}
            >
              <option value="ORIGINAL">Original</option>
              <option value="COP">COP</option>
              <option value="USD">USD</option>
            </select>
          </label>
        </div>
        <div className="metrics-control-row">
          <label className="field">
            <span>Agrupar por</span>
            <select value={groupMode} onChange={(event) => setGroupMode(event.target.value as GroupMode)}>
              <option value="day">Día</option>
              <option value="week">Semana</option>
              <option value="month">Mes</option>
              <option value="year">Año</option>
            </select>
          </label>
        </div>
        <div className="metrics-control-row">
          <div className="metrics-load-action">
            <button
              className="primary-button"
              type="button"
              onClick={() => void loadFirebaseCosts()}
              disabled={isLoadingFirebaseCosts}
            >
              {isLoadingFirebaseCosts ? 'Cargando...' : 'Cargar'}
            </button>
          </div>
        </div>
      </div>

      <div className="metrics-grid">
        <div className="metric-card">
          <span>TASA</span>
          <span>DE</span>
          <span>CONVERSION</span>
          <strong>{conversionRate.toFixed(2)}%</strong>
        </div>
        <div className="metric-card">
          <span>COSTO</span>
          <span>GEMINI</span>
          <span>AI</span>
          <strong>{formatCurrency(totals.geminiCost, totals.costCurrency)}</strong>
        </div>
        <div className="metric-card">
          <span>COSTO VECTORIZER</span>
          <span>AI</span>
          <span>&nbsp;</span>
          <strong>{formatCurrency(totals.vectorizerCost, 'USD')}</strong>
          <small className="metric-card-pill">Credits: {totals.vectorizerCredits}</small>
        </div>
        <div className="metric-card">
          <span>COSTO</span>
          <span>GOOGLE</span>
          <span>CLOUD</span>
          <strong>
            {formatOptionalCurrency(firebaseTotalCostValue.value, firebaseTotalCostValue.currency)}
          </strong>
          <small className="metric-card-note">
            Bruto {formatOptionalCurrency(firebaseGrossCostValue.value, firebaseGrossCostValue.currency)}
          </small>
          <small className="metric-card-note">
            Ahorro {formatOptionalCurrency(firebaseSavingsValue.value, firebaseSavingsValue.currency)}
          </small>
        </div>
        <div className="metric-card">
          <span>COSTO</span>
          <span>EMAILING</span>
          <span>BREVO</span>
          <strong>{formatCurrency(brevoEmailingCost, totals.costCurrency)}</strong>
        </div>
        <div className="metric-card">
          <span>COSTO TOTAL</span>
          <span>WEDOIT</span>
          <span>&nbsp;</span>
          <strong>
            {firebaseCurrencyMatchesDisplay
              ? formatCurrency(totals.totalCost + firebaseTotalCost + brevoEmailingCost, totals.costCurrency)
              : '-'}
          </strong>
        </div>
        <div className="metric-card">
          <span>INGRESO</span>
          <span>TOTAL</span>
          <span>WEDOIT</span>
          <strong>{formatOptionalCurrency(totals.totalRevenue, totals.totalRevenueCurrency)}</strong>
          <small className="metric-card-note">
            Bold {formatOptionalCurrency(totals.boldRevenue, totals.boldRevenueCurrency)}
          </small>
          <small className="metric-card-note">
            Directo {formatOptionalCurrency(totals.adminRevenue, totals.adminRevenueCurrency)}
          </small>
        </div>
        <div className="metric-card">
          <span>MARGEN NETO</span>
          <span>WEDOIT</span>
          <span>&nbsp;</span>
          <strong>{formatOptionalCurrency(netMargin, totals.costCurrency)}</strong>
        </div>
      </div>

      <div className="metrics-block">
        <div className="project-table-header">
          <h3>Costos de servicios Google Cloud</h3>
          <button
            className="secondary-button metrics-hide-zero-button"
            type="button"
            onClick={() => setHideZeroGoogleCloudRows((value) => !value)}
          >
            {hideZeroGoogleCloudRows ? 'Mostrar 0' : 'Ocultar 0'}
          </button>
        </div>
        {isLoadingFirebaseCosts || firebaseCostsStatus ? (
          <div className={`inline-toast ${firebaseCostsStatus ? 'inline-toast-warning' : 'inline-toast-neutral'} metrics-sync`}>
            {isLoadingFirebaseCosts ? 'Cargando costos Google Cloud...' : firebaseCostsStatus}
          </div>
        ) : null}
        <div className="table metrics-table">
          <div className="table-row table-header metrics-firebase-row">
            <div>Servicio</div>
            <div>Bruto</div>
            <div>Ahorro</div>
            <div>Total</div>
          </div>
          {visibleFirebaseServiceRows.length ? (
            visibleFirebaseServiceRows.map((row, index) => (
              <div
                className={`table-row metrics-firebase-row ${
                  row.isExcluded ? 'metrics-excluded-cost-row' : ''
                }`}
                key={`${row.Servicio}-${index}`}
              >
                <div>
                  {row.Servicio}
                  {row.isExcluded ? <span className="metrics-excluded-note">Excluido en cálculo de costos</span> : null}
                </div>
                <div>{row.Bruto}</div>
                <div>{row.Ahorro}</div>
                <div>{row.Total}</div>
              </div>
            ))
          ) : (
            <div className="table-row metrics-empty-row">
              <div className="table-placeholder">Sin costos cargados de servicios Google Cloud.</div>
            </div>
          )}
        </div>
      </div>

      <div className="metrics-block">
        <div className="project-table-header">
          <h3>Desglose Google Cloud por periodo</h3>
          <button
            className="secondary-button metrics-hide-zero-button"
            type="button"
            onClick={() => setHideZeroGoogleCloudPeriodRows((value) => !value)}
          >
            {hideZeroGoogleCloudPeriodRows ? 'Mostrar 0' : 'Ocultar 0'}
          </button>
        </div>
        <div className="table metrics-table">
          <div className="table-row table-header metrics-google-cloud-period-row">
            <div>Periodo</div>
            <div>Servicio</div>
            <div>Bruto</div>
            <div>Ahorro</div>
            <div>Total</div>
          </div>
          {visibleFirebasePeriodServiceRows.length ? (
            visibleFirebasePeriodServiceRows.map((row, index) => (
              <div
                className={`table-row metrics-google-cloud-period-row ${
                  row.isExcluded ? 'metrics-excluded-cost-row' : ''
                }`}
                key={`${row.Periodo}-${row.Servicio}-${index}`}
              >
                <div className="mono">{row.Periodo}</div>
                <div>
                  {row.Servicio}
                  {row.isExcluded ? <span className="metrics-excluded-note">Excluido en cálculo de costos</span> : null}
                </div>
                <div>{row.Bruto}</div>
                <div>{row.Ahorro}</div>
                <div>{row.Total}</div>
              </div>
            ))
          ) : (
            <div className="table-row metrics-empty-row">
              <div className="table-placeholder">Sin desglose cargado de servicios por periodo.</div>
            </div>
          )}
        </div>
      </div>

      <div className="metrics-block">
        <div className="project-table-header">
          <h3>Métricas agrupadas</h3>
          <button
            className="secondary-button metrics-hide-zero-button"
            type="button"
            onClick={() => setHideZeroGroupedRows((value) => !value)}
          >
            {hideZeroGroupedRows ? 'Mostrar 0' : 'Ocultar 0'}
          </button>
        </div>
        <div className="table metrics-table">
          <div className="table-row table-header metrics-daily-row">
            <div>Periodo</div>
            <div>Proy. Nuevos</div>
            <div>Número Compras</div>
            <div>Tasa Conv.</div>
            <div>Costo Gemini AI</div>
            <div>Costo Google Cloud</div>
            <div>Costo Total</div>
            <div>Ingreso Bold</div>
            <div>Ingreso Directo</div>
            <div>Ingreso Total</div>
            <div>Margen</div>
          </div>
          {visibleGroupedMetrics.length ? (
            visibleGroupedMetrics.map((day) => {
              const firebaseCost = day.firebaseCost;
              const displayRowMargin =
                day.totalRevenue !== null &&
                day.totalRevenueCurrency === day.costCurrency &&
                firebaseCurrencyMatchesDisplay
                  ? day.totalRevenue - day.totalCost - firebaseCost
                  : null;
              return (
                <div
                  className={`table-row metrics-daily-row ${
                    displayRowMargin !== null && displayRowMargin > 0
                      ? 'metrics-row-positive'
                      : displayRowMargin !== null && displayRowMargin < 0
                        ? 'metrics-row-negative'
                        : ''
                  }`}
                  key={day.date}
                >
                  <div className="mono">{day.date}</div>
                  <div>{day.previews}</div>
                  <div>{day.purchased}</div>
                  <div>{day.conversionRate.toFixed(2)}%</div>
                  <div>{formatCurrency(day.totalCost, day.costCurrency)}</div>
                  <div>{formatOptionalCurrency(firebaseCost, firebaseTotalCostValue.currency)}</div>
                  <div>
                    {firebaseCurrencyMatchesDisplay
                      ? formatCurrency(day.totalCost + firebaseCost, day.costCurrency)
                      : '-'}
                  </div>
                  <div>{formatOptionalCurrency(day.boldRevenue, day.boldRevenueCurrency)}</div>
                  <div>{formatOptionalCurrency(day.adminRevenue, day.adminRevenueCurrency)}</div>
                  <div>{formatOptionalCurrency(day.totalRevenue, day.totalRevenueCurrency)}</div>
                  <div>{formatOptionalCurrency(displayRowMargin, day.costCurrency)}</div>
                </div>
              );
            })
          ) : (
            <div className="table-row metrics-empty-row">
              <div className="table-placeholder">Sin previews registrados.</div>
            </div>
          )}
        </div>
      </div>

      <div className="metrics-block">
        <div className="project-table-header">
          <h3>Proyectos</h3>
          <div className="project-total">{visibleProjectRows.length} proyectos</div>
        </div>
        <div className="table metrics-table">
          <div className="table-row table-header metrics-project-row">
            <div>FECHA</div>
            <div>ID</div>
            <div>EMAIL</div>
            <div>PAGO</div>
            <div className="metrics-cost-breakdown">
              <span>Preview</span>
              <span>Full</span>
              <span>Regeneración</span>
            </div>
            <div>COSTO TOTAL</div>
            <div className="metrics-cost-breakdown">
              <span>Bold</span>
              <span>Directo</span>
            </div>
            <div>INGRESO TOTAL</div>
            <div>MARGEN</div>
          </div>
          {visibleProjectRows.length ? (
            visibleProjectRows.map((row) => {
              const previewCost = getGeminiCostValue(row.previewCost, displayCurrency, usdToCop);
              const fullCost = getGeminiCostValue(row.fullCost, displayCurrency, usdToCop);
              const regenerationCost = getGeminiCostValue(row.regenerationCost, displayCurrency, usdToCop);
              const totalCost = getGeminiCostValue(row.totalCost, displayCurrency, usdToCop);
              const boldRevenue = getRevenueValue(row.boldRevenue, row.boldRevenueCurrency, displayCurrency, usdToCop);
              const adminRevenue = getRevenueValue(row.adminRevenue, row.adminRevenueCurrency, displayCurrency, usdToCop);
              const totalRevenue = sumTwoMoneyValues(
                boldRevenue.value,
                boldRevenue.currency,
                adminRevenue.value,
                adminRevenue.currency
              );
              const margin =
                totalRevenue.value !== null && totalRevenue.currency === totalCost.currency
                  ? totalRevenue.value - totalCost.value
                  : null;

              return (
                <div
                  className={`table-row metrics-project-row ${
                    row.converted ? 'metrics-row-positive' : 'metrics-row-negative'
                  }`}
                  key={row.projectId}
                >
                  <div>{formatDateTime(row.createdTimestamp)}</div>
                  <div className="mono">{row.projectId}</div>
                  <div className="metrics-admin-email">{row.email || '-'}</div>
                  <div>
                    {row.paymentTimestamp ? formatPaymentDate(row.paymentTimestamp) : row.converted ? 'Sí' : 'No'}
                  </div>
                  <div className="metrics-cost-breakdown">
                    <span>{formatCurrency(previewCost.value, previewCost.currency)}</span>
                    <span>{formatCurrency(fullCost.value, fullCost.currency)}</span>
                    <span>{formatCurrency(regenerationCost.value, regenerationCost.currency)}</span>
                  </div>
                  <div>{formatCurrency(totalCost.value, totalCost.currency)}</div>
                  <div className="metrics-cost-breakdown">
                    <span>{formatOptionalCurrency(boldRevenue.value, boldRevenue.currency)}</span>
                    <span>{formatOptionalCurrency(adminRevenue.value, adminRevenue.currency)}</span>
                  </div>
                  <div>{formatOptionalCurrency(totalRevenue.value, totalRevenue.currency)}</div>
                  <div>{formatOptionalCurrency(margin, totalCost.currency)}</div>
                </div>
              );
            })
          ) : (
            <div className="table-row metrics-empty-row">
              <div className="table-placeholder">Sin previews registrados.</div>
            </div>
          )}
        </div>
      </div>

      <div className="metrics-export-footer">
        <button
          className="primary-button"
          type="button"
          onClick={() =>
            downloadExcelWorkbook(
              'wedoit-metricas-y-costos.xls',
              [
                {
                  name: 'Proyectos',
                  headers: projectExportHeaders,
                  rows: exportProjectRows,
                },
                {
                  name: 'Metricas agrupadas',
                  headers: groupedExportHeaders,
                  rows: exportGroupedRows,
                },
                {
                  name: 'Google Cloud',
                  headers: googleCloudExportHeaders,
                  rows: visibleFirebaseServiceRows,
                },
                {
                  name: 'GC por periodo',
                  headers: googleCloudPeriodExportHeaders,
                  rows: visibleFirebasePeriodServiceRows,
                },
              ]
            )
          }
          disabled={!hasExportData}
        >
          Descargar Excel
        </button>
      </div>
    </div>
  );
}

export default MetricsSection;
